import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wallet, CreditCard, Calendar, Hash, FileText, AlertTriangle, Info,
} from "lucide-react";

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatMXN, toISODate } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { useAplicarPago } from "@/features/facturas/api";
import { METODO_LABEL } from "@/features/facturas/helpers";
import type { MetodoPago } from "@/features/facturas/schemas";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clienteId: string;
  clienteNombre: string;
  /**
   * Callback opcional al éxito — útil para que el padre refresque
   * estado_cuenta del cliente.
   */
  onSuccess?: () => void;
};

/**
 * Modal pa' aplicar un pago como SALDO A FAVOR (sin factura asociada).
 *
 *   - No requiere factura — el pago queda con `factura_id=null` en BD.
 *   - Se consume automáticamente cuando se genera la próxima factura
 *     del cliente (lógica rc23 en backend factura_service.aplicar_saldo_a_favor).
 *   - NO se notifica a Toku en este momento (Toku solo recibe el evento
 *     cuando el saldo a favor se aplica a una factura real).
 *
 * Se usa desde:
 *   - PagoRapidoDialog (topbar) cuando el cliente está al corriente y
 *     el cajero igual quiere registrarle un pago anticipado.
 *   - ClienteDetalle como acción directa "Aplicar saldo a favor".
 */
export function AplicarSaldoFavorDialog({
  open, onOpenChange, clienteId, clienteNombre, onSuccess,
}: Props) {
  const mut = useAplicarPago();

  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState<MetodoPago>("transferencia");
  const [referencia, setReferencia] = useState("");
  const [fecha, setFecha] = useState(toISODate(new Date()));
  const [notas, setNotas] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMonto("");
      setMetodo("transferencia");
      setReferencia("");
      setFecha(toISODate(new Date()));
      setNotas("");
      setFormError(null);
    }
  }, [open]);

  async function handleSubmit() {
    setFormError(null);
    const montoNum = Number(monto);
    if (!Number.isFinite(montoNum) || montoNum <= 0) {
      setFormError("El monto debe ser mayor a 0.");
      return;
    }

    try {
      const res = await mut.mutateAsync({
        cliente_id: clienteId,
        factura_id: null,
        monto: String(montoNum),
        metodo,
        referencia: referencia.trim() || null,
        fecha: fecha || null,
        notas: notas.trim() || null,
      });
      toast.success(`Saldo a favor de ${formatMXN(montoNum)} registrado`, {
        description: `Folio ${res.pago.folio} · se aplicará a la próxima factura`,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const err = e as ApiError;
      setFormError(err?.detail ?? err?.message ?? "Error al registrar el saldo a favor.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet size={16} /> Aplicar saldo a favor
          </DialogTitle>
          <DialogDescription>
            {clienteNombre} — pago anticipado sin factura asociada.
          </DialogDescription>
        </DialogHeader>

        {/* Info: cómo funciona el saldo a favor */}
        <div className="rounded-md border border-blue-500/30 bg-blue-500/8 p-3 flex gap-2">
          <Info size={14} className="shrink-0 text-blue-500 mt-0.5" />
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            El pago queda como crédito del cliente y se aplica
            automáticamente a la próxima factura que se le emita
            (parcial o total según monto).
            <br />
            <span className="text-muted-foreground/70">
              Toku se notifica hasta que el saldo se aplique a una factura real.
            </span>
          </div>
        </div>

        <div className="grid gap-3 py-2">
          {/* Monto */}
          <label className="grid gap-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Wallet size={11} /> Monto
            </span>
            <Input
              type="number" step="0.01" min="0.01"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0.00"
              autoFocus
              className="font-mono text-lg"
            />
          </label>

          {/* Método */}
          <label className="grid gap-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <CreditCard size={11} /> Método de pago
            </span>
            <Select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoPago)}
            >
              {(Object.keys(METODO_LABEL) as MetodoPago[]).map((m) => (
                <option key={m} value={m}>{METODO_LABEL[m]}</option>
              ))}
            </Select>
          </label>

          {/* Fecha */}
          <label className="grid gap-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Calendar size={11} /> Fecha del pago
            </span>
            <Input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </label>

          {/* Referencia */}
          <label className="grid gap-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Hash size={11} /> Referencia (opcional)
            </span>
            <Input
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Folio de transferencia, # de cheque, etc."
            />
          </label>

          {/* Notas */}
          <label className="grid gap-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <FileText size={11} /> Notas (opcional)
            </span>
            <Textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Información adicional…"
              rows={2}
            />
          </label>
        </div>

        {formError && (
          <div className="rounded-md border border-red-500/30 bg-red-500/8 p-2 flex gap-2 text-xs text-red-500">
            <AlertTriangle size={14} className="shrink-0" />
            {formError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={mut.isPending || !monto}>
            {mut.isPending ? "Aplicando…" : "Aplicar saldo a favor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
